export const industriesData = [
  {
    id: "law-enforcement",
    icon: "ri-shield-user-line",
    title: "Law Enforcement",
    tagline: "Accountability, Evidence & Field Communication",
    heroImage: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/276d9496cbccadf3d551a40a6683a718.png",
    description: "Law enforcement agencies across Malaysia rely on SE Distribution for mission-critical technology that enhances officer accountability, streamlines evidence management, and improves field communication. From body-worn video devices to secure digital evidence platforms, we equip public safety teams with tools that meet the highest standards of reliability and compliance.",
    challenges: [
      "Maintaining transparent and tamper-proof evidence chains",
      "Enabling real-time communication across large patrol areas",
      "Managing and storing large volumes of video footage securely",
      "Equipping officers with durable devices that survive field conditions",
    ],
    solutions: [
      {
        title: "Body-Worn Video (BWV) Devices",
        brand: "Hytera",
        description: "Hytera BWC devices are the primary choice for law enforcement body-worn cameras in Malaysia. Featuring encrypted recording, GPS tagging, live streaming, and pre-event buffering — every incident is captured with full context.",
        icon: "ri-vidicon-line",
      },
      {
        title: "Digital Evidence Management",
        brand: "Hytera",
        description: "Hytera DEMS provides centralised digital evidence management for all body-worn camera footage — with secure upload, chain-of-custody audit trails, and AI-assisted search.",
        icon: "ri-folder-shield-line",
      },
      {
        title: "Rugged Field Devices",
        brand: "Honeywell",
        description: "Honeywell rugged mobile computers enable officers to access databases, submit reports, and communicate in real time — even in the most demanding field environments.",
        icon: "ri-smartphone-line",
      },
    ],
    outcomes: [
      { metric: "68%", label: "Reduction in evidence disputes" },
      { metric: "3x", label: "Faster evidence retrieval" },
      { metric: "99.9%", label: "Device uptime in field operations" },
    ],
    featuredBrands: ["Hytera", "Honeywell"],
  },
  {
    id: "construction",
    icon: "ri-building-2-line",
    title: "Construction",
    tagline: "Rugged Devices for Demanding Job Sites",
    heroImage: "https://readdy.ai/api/search-image?query=construction%20site%20engineer%20wearing%20hard%20hat%20holding%20rugged%20tablet%20computer%20reviewing%20blueprints%2C%20heavy%20machinery%20in%20background%2C%20bright%20daylight%2C%20professional%20industrial%20photography%2C%20clean%20composition%2C%20photorealistic&width=1200&height=500&seq=ind-con1&orientation=landscape",
    description: "Construction and infrastructure projects demand technology that can keep up with the pace and harshness of the job site. SE Distribution provides rugged computing solutions and communication tools that help project managers, engineers, and field crews stay connected, productive, and safe — from groundbreaking to handover.",
    challenges: [
      "Devices failing due to dust, water, drops, and extreme heat",
      "Disconnected field teams causing project delays and rework",
      "Paper-based processes slowing down reporting and approvals",
      "Data loss from inadequate backup systems at project offices",
    ],
    solutions: [
      {
        title: "Rugged Laptops & Tablets",
        brand: "Dell Rugged Pro",
        description: "Dell Rugged Pro laptops and tablets are MIL-SPEC 810H certified to survive drops, vibration, dust, and water. Field engineers can run full CAD and BIM software on site without compromise.",
        icon: "ri-macbook-line",
      },
      {
        title: "Rugged Mobile Computers",
        brand: "Honeywell",
        description: "Honeywell rugged handhelds and tablets enable real-time inventory tracking, work order management, and field data capture — even in the harshest construction environments.",
        icon: "ri-smartphone-line",
      },
      {
        title: "Site Security & Documentation",
        brand: "Hytera",
        description: "Hytera body-worn cameras provide on-site security documentation, incident recording, and real-time live streaming to project safety managers overseeing multiple construction sites.",
        icon: "ri-shield-user-line",
      },
      {
        title: "Project Data Backup",
        brand: "Synology",
        description: "Synology NAS solutions protect critical project files, drawings, and documentation with automated backup and easy recovery — keeping project data safe at every stage.",
        icon: "ri-cloud-line",
      },
    ],
    outcomes: [
      { metric: "40%", label: "Faster project reporting" },
      { metric: "60%", label: "Reduction in device replacement costs" },
      { metric: "Zero", label: "Data loss incidents post-deployment" },
    ],
    featuredBrands: ["Dell Rugged Pro", "Honeywell", "Synology"],
  },
  {
    id: "healthcare",
    icon: "ri-heart-pulse-line",
    title: "Healthcare",
    tagline: "Secure Data & Reliable Devices for Patient Care",
    heroImage: "https://readdy.ai/api/search-image?query=healthcare%20nurse%20using%20rugged%20tablet%20computer%20in%20hospital%20ward%2C%20medical%20environment%2C%20clean%20white%20clinical%20setting%2C%20professional%20medical%20photography%2C%20soft%20lighting%2C%20photorealistic%2C%20high%20detail&width=1200&height=500&seq=ind-hc1&orientation=landscape",
    description: "Healthcare organizations require technology that is reliable, hygienic, and secure. SE Distribution provides healthcare-grade rugged devices, secure data storage, and backup solutions that help clinical staff deliver better patient care while maintaining strict data compliance and operational continuity.",
    challenges: [
      "Protecting sensitive patient data from breaches and ransomware",
      "Devices that can be sanitized without damage to electronics",
      "Ensuring 24/7 system availability for critical clinical applications",
      "Managing large volumes of medical imaging and patient records",
    ],
    solutions: [
      {
        title: "Healthcare Rugged Mobile Devices",
        brand: "Honeywell",
        description: "Honeywell healthcare-grade mobile computers feature antimicrobial housings, easy disinfection, and long battery life — designed for nurses, pharmacists, and clinical staff on the move.",
        icon: "ri-smartphone-line",
      },
      {
        title: "Medical Data Backup & Recovery",
        brand: "ExaGrid",
        description: "ExaGrid tiered backup storage ensures that patient records, imaging data, and clinical systems are protected with fast recovery times — meeting healthcare compliance requirements.",
        icon: "ri-hard-drive-2-line",
      },
      {
        title: "NAS Storage for Medical Imaging",
        brand: "Synology",
        description: "Synology NAS provides scalable, high-capacity storage for PACS, DICOM imaging, and EHR systems — with HIPAA-aligned access controls and audit logging.",
        icon: "ri-cloud-line",
      },
      {
        title: "High-Density Storage Infrastructure",
        brand: "Arcfra",
        description: "Arcfra enterprise storage solutions support the high-throughput demands of hospital data centers, ensuring fast access to critical clinical data at all times.",
        icon: "ri-server-line",
      },
    ],
    outcomes: [
      { metric: "100%", label: "Compliance with data protection audits" },
      { metric: "15 min", label: "Average recovery time objective (RTO)" },
      { metric: "Zero", label: "Unplanned downtime in 12 months" },
    ],
    featuredBrands: ["Honeywell", "ExaGrid", "Synology", "Arcfra"],
  },
  {
    id: "logistics",
    icon: "ri-truck-line",
    title: "Logistics & Transportation",
    tagline: "Real-Time Tracking & Warehouse Efficiency",
    heroImage: "https://readdy.ai/api/search-image?query=warehouse%20logistics%20worker%20scanning%20barcode%20with%20rugged%20handheld%20device%2C%20large%20distribution%20center%20with%20shelves%20and%20packages%2C%20bright%20industrial%20lighting%2C%20professional%20commercial%20photography%2C%20photorealistic&width=1200&height=500&seq=ind-log1&orientation=landscape",
    description: "Logistics and transportation companies operate in fast-paced, high-volume environments where accuracy and uptime are everything. SE Distribution equips warehouses, fleets, and distribution centers with rugged scanning devices, real-time communication tools, and resilient data infrastructure to keep operations running without interruption.",
    challenges: [
      "High device failure rates in warehouse and outdoor environments",
      "Disconnected communication between drivers, dispatchers, and warehouses",
      "Inventory inaccuracies due to manual or outdated scanning systems",
      "Data loss from inadequate backup at distribution hubs",
    ],
    solutions: [
      {
        title: "Rugged Warehouse Scanners & Handhelds",
        brand: "Honeywell",
        description: "Honeywell rugged mobile computers and barcode scanners are built for warehouse floors — with fast scan engines, drop resistance, and all-day battery life for high-volume operations.",
        icon: "ri-barcode-line",
      },
      {
        title: "Fleet Communication Devices",
        brand: "Hytera",
        description: "Hytera professional mobile radios and push-to-talk devices keep drivers, dispatchers, and logistics coordinators connected across large coverage areas with crystal-clear audio.",
        icon: "ri-radio-line",
      },
      {
        title: "Rugged Field Tablets",
        brand: "Dell Rugged Pro",
        description: "Dell Rugged Pro tablets enable drivers and field agents to manage delivery confirmations, route updates, and proof-of-delivery digitally — even in extreme outdoor conditions.",
        icon: "ri-macbook-line",
      },
      {
        title: "Logistics Data Backup",
        brand: "Synology",
        description: "Synology NAS and backup solutions protect warehouse management systems, inventory databases, and logistics records with automated, reliable backup and fast recovery.",
        icon: "ri-cloud-line",
      },
    ],
    outcomes: [
      { metric: "35%", label: "Improvement in picking accuracy" },
      { metric: "50%", label: "Reduction in device downtime" },
      { metric: "2x", label: "Faster order processing cycles" },
    ],
    featuredBrands: ["Honeywell", "Hytera", "Dell Rugged Pro", "Synology"],
  },
  {
    id: "government",
    icon: "ri-government-line",
    title: "Government & Public Sector",
    tagline: "Compliant, Secure & Mission-Ready Technology",
    heroImage: "https://readdy.ai/api/search-image?query=government%20official%20in%20formal%20attire%20using%20secure%20laptop%20computer%20in%20modern%20office%20building%2C%20professional%20corporate%20environment%2C%20clean%20minimal%20background%2C%20soft%20natural%20lighting%2C%20photorealistic%20commercial%20photography&width=1200&height=500&seq=ind-gov1&orientation=landscape",
    description: "Government agencies and public sector organizations require technology solutions that meet strict procurement standards, security compliance, and long-term reliability. SE Distribution is an authorized distributor of enterprise-grade brands trusted by government bodies across Malaysia — providing solutions that are secure, auditable, and built to last.",
    challenges: [
      "Meeting government procurement and compliance requirements",
      "Protecting sensitive citizen and national security data",
      "Ensuring business continuity for critical public services",
      "Deploying durable devices for field government officers",
    ],
    solutions: [
      {
        title: "Secure Data Backup & Recovery",
        brand: "ExaGrid",
        description: "ExaGrid backup appliances provide immutable, ransomware-resilient storage for government data — ensuring that critical records and systems can be recovered rapidly after any incident.",
        icon: "ri-hard-drive-2-line",
      },
      {
        title: "Government-Grade Body Cameras",
        brand: "Hytera",
        description: "Hytera body-worn cameras are deployed by enforcement officers at government facilities, public spaces, and checkpoints — providing reliable evidence capture with centralized DEMS management and full audit trails.",
        icon: "ri-camera-3-line",
      },
      {
        title: "Field Officer Devices",
        brand: "Honeywell",
        description: "Honeywell rugged mobile devices equip field government officers — from enforcement to inspection — with reliable, durable tools for data capture and real-time reporting.",
        icon: "ri-smartphone-line",
      },
      {
        title: "Body-Worn Video for Enforcement",
        brand: "Hytera",
        description: "Hytera BWC devices are used by enforcement agencies and regulatory bodies to document field activities, ensuring accountability and providing irrefutable evidence when needed.",
        icon: "ri-vidicon-line",
      },
    ],
    outcomes: [
      { metric: "100%", label: "Compliance with government procurement standards" },
      { metric: "99.99%", label: "System uptime for critical services" },
      { metric: "5+", label: "Government agencies served in Malaysia" },
    ],
    featuredBrands: ["ExaGrid", "Honeywell", "Hytera"],
  },
  {
    id: "it",
    icon: "ri-database-line",
    title: "IT & Data Management",
    tagline: "Enterprise Storage, Backup & Infrastructure",
    heroImage: "https://readdy.ai/api/search-image?query=modern%20enterprise%20data%20center%20server%20room%20with%20rows%20of%20rack%20servers%2C%20glowing%20LED%20indicators%2C%20clean%20white%20and%20gray%20hardware%2C%20professional%20IT%20infrastructure%20photography%2C%20dramatic%20lighting%2C%20high%20detail%2C%20photorealistic&width=1200&height=500&seq=ind-it1&orientation=landscape",
    description: "IT departments and managed service providers need storage and backup infrastructure that scales with their data growth, protects against ransomware, and delivers fast recovery when it matters most. SE Distribution's data protection portfolio — featuring ExaGrid, Arcfra, and Synology — provides enterprise-grade solutions for organizations of every size.",
    challenges: [
      "Exponential data growth outpacing storage capacity",
      "Ransomware attacks targeting backup systems",
      "Long recovery times disrupting business operations",
      "Managing complex multi-site backup environments",
    ],
    solutions: [
      {
        title: "Tiered Backup Storage",
        brand: "ExaGrid",
        description: "ExaGrid's unique tiered backup storage with a non-network-facing repository provides a last line of defense against ransomware — with instant recovery and scale-out architecture.",
        icon: "ri-hard-drive-2-line",
      },
      {
        title: "High-Performance Storage Infrastructure",
        brand: "Arcfra",
        description: "Arcfra all-flash and hybrid storage arrays deliver the IOPS and throughput needed for demanding enterprise workloads — from databases to virtualization platforms.",
        icon: "ri-server-line",
      },
      {
        title: "NAS & Hybrid Cloud Storage",
        brand: "Synology",
        description: "Synology NAS solutions provide scalable, cost-effective storage for SMEs and enterprises — with built-in backup, sync, and cloud integration for a complete data management platform.",
        icon: "ri-cloud-line",
      },
      {
        title: "Video Evidence Storage",
        brand: "Synology",
        description: "Synology NAS systems are optimized for high-capacity, continuous write workloads from body-worn cameras and IP cameras — providing reliable, centralized video evidence storage for security operations.",
        icon: "ri-hard-drive-3-line",
      },
    ],
    outcomes: [
      { metric: "80%", label: "Reduction in backup storage costs" },
      { metric: "15 min", label: "Recovery time for critical systems" },
      { metric: "100%", label: "Ransomware recovery success rate" },
    ],
    featuredBrands: ["ExaGrid", "Arcfra", "Synology"],
  },
];
